import {useCallback, useMemo} from 'react';
import {useIntl} from 'react-intl';

export type Validated<T> = T & {
	error: string | null;
};

interface ValidatableItem {
	id?: unknown;
	displayName?: string;
}

// The form two option names are compared in: trimmed, runs of spaces inside collapsed to one, and lower-cased,
// so "Design", " design" and "DESIGN" are the same option.
export const optionNameKey = (name: string): string => name.trim().replace(/\s+/g, ' ').toLowerCase();

export const sameOptionName = (a: string | undefined, b: string | undefined): boolean =>
	optionNameKey(a ?? '') === optionNameKey(b ?? '');

const NO_SAVED_OPTIONS: readonly ValidatableItem[] = [];

// Two saved options left unchanged clash only on an exactly equal name, so a stored pair that differs only in case
// or spaces does not block a save until one of them is touched.
export function useOptionListValidator<T extends ValidatableItem>(
	options: T[],
	savedOptions: readonly ValidatableItem[] = NO_SAVED_OPTIONS
): Validated<T>[] {
	const {formatMessage} = useIntl();
	const touched = useCallback(
		(option: T) => {
			const saved = option.id ? savedOptions.find(o => o.id === option.id) : undefined;
			return saved === undefined || saved.displayName !== option.displayName;
		},
		[savedOptions]
	);
	const getError = useCallback(
		(option: T) => {
			if (!option.displayName) {
				return formatMessage({
					id: 'settings.custom-fields.dropdown-options.edit_modal.dropdown_option_item.error_message',
				});
			}
			// Every other entry counts, saved or not. An option added in this session has no id yet, so entries
			// are told apart by themselves rather than by id.
			const clashes = (o: T) =>
				o.displayName === option.displayName ||
				((touched(o) || touched(option)) && sameOptionName(o.displayName, option.displayName));
			if (options.some(o => o !== option && clashes(o))) {
				return formatMessage({
					id: 'settings.custom-fields.dropdown-options.edit_modal.dropdown_option_item.duplicate_error_message',
				});
			}
			return null;
		},
		[options, touched]
	);

	const validatedOptions = useMemo(() => options.map(o => ({...o, error: getError(o)})), [options, getError]);

	return validatedOptions;
}
